import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import {
  Sparkles,
  Activity,
  Check,
  RefreshCw,
  Thermometer,
  ShieldCheck,
  Sliders,
  TrendingUp,
  Server,
  Leaf,
} from 'lucide-react';
import {
  generateAIMiningNetworkOptimization,
  generateAIMiningHardwareThermalEfficiency,
  AIMiningNetworkOptimizationResult,
  AIMiningHardwareThermalEfficiencyResult,
} from '@/services/aiAssistantService';

interface AIMiningAdvisorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  totalHashRateGH: number;
  activeMiners: number;
  networkDifficulty: number;
  currentBlockTimeSec?: number;
  averageTempC?: number;
  averagePowerW?: number;
  onApplyOptimalThreads?: (threads: { entry: number; balanced: number; extreme: number; dutyCycle: number }) => void;
}

export function AIMiningAdvisorDialog({
  isOpen,
  onClose,
  totalHashRateGH,
  activeMiners,
  networkDifficulty,
  currentBlockTimeSec = 10.2,
  averageTempC = 58,
  averagePowerW = 2840,
  onApplyOptimalThreads,
}: AIMiningAdvisorDialogProps) {
  const [activeTab, setActiveTab] = useState<'network' | 'thermal'>('network');
  const [networkLoading, setNetworkLoading] = useState(false);
  const [thermalLoading, setThermalLoading] = useState(false);
  const [networkResult, setNetworkResult] = useState<AIMiningNetworkOptimizationResult | null>(null);
  const [thermalResult, setThermalResult] = useState<AIMiningHardwareThermalEfficiencyResult | null>(null);
  const [appliedThreads, setAppliedThreads] = useState(false);

  const handleRunNetworkAnalysis = async () => {
    setNetworkLoading(true);
    try {
      const res = await generateAIMiningNetworkOptimization({
        totalHashRateGH,
        activeMiners,
        networkDifficulty,
        currentBlockTimeSec,
      });
      setNetworkResult(res);
    } finally {
      setNetworkLoading(false);
    }
  };

  const handleRunThermalAnalysis = async () => {
    setThermalLoading(true);
    try {
      const res = await generateAIMiningHardwareThermalEfficiency({
        averageTempC,
        averagePowerW,
        totalHashRateGH,
      });
      setThermalResult(res);
    } finally {
      setThermalLoading(false);
    }
  };

  const handleApplyThreads = () => {
    if (!networkResult) return;
    const { entryClassThreads, balancedClassThreads, extremeClassThreads, throttleDutyCyclePercent } =
      networkResult.optimalWorkerThreadAllocation;
    onApplyOptimalThreads?.({
      entry: entryClassThreads,
      balanced: balancedClassThreads,
      extreme: extremeClassThreads,
      dutyCycle: throttleDutyCyclePercent,
    });
    setAppliedThreads(true);
    setTimeout(() => setAppliedThreads(false), 3000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 p-6 sm:p-8">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-600 via-teal-600 to-cyan-600 flex items-center justify-center text-white shadow-lg shadow-emerald-500/20">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                DeepSeek Mining & Hashrate Doctor
                <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border-0">
                  PoW Telemetry V4
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
                Institutional network difficulty forecast, compact block propagation tuning & thermal power arbitrage.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(val) => setActiveTab(val as 'network' | 'thermal')} className="mt-4">
          <TabsList className="grid grid-cols-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
            <TabsTrigger value="network" className="flex items-center gap-2 text-xs sm:text-sm font-medium">
              <Activity className="w-4 h-4 text-emerald-600" />
              Hashrate & Difficulty Forecaster
            </TabsTrigger>
            <TabsTrigger value="thermal" className="flex items-center gap-2 text-xs sm:text-sm font-medium">
              <Thermometer className="w-4 h-4 text-teal-600" />
              Thermal & Power Arbitrage
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Network & Difficulty Forecaster */}
          <TabsContent value="network" className="space-y-4 pt-3">
            <Card className="p-4 bg-gradient-to-br from-emerald-50/50 to-teal-50/30 dark:from-emerald-950/20 dark:to-teal-950/10 border-emerald-100 dark:border-emerald-900/40">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase font-semibold text-emerald-700 dark:text-emerald-400 tracking-wider">
                    Current Network Context
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Hashrate:{' '}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {totalHashRateGH.toLocaleString()} GH/s
                    </span>{' '}
                    | Nodes:{' '}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {activeMiners.toLocaleString()}
                    </span>{' '}
                    | Difficulty:{' '}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {networkDifficulty.toLocaleString()}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleRunNetworkAnalysis}
                  disabled={networkLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-600/20 flex items-center gap-2 shrink-0"
                >
                  <RefreshCw className={`w-4 h-4 ${networkLoading ? 'animate-spin' : ''}`} />
                  {networkResult ? 'Re-Run Forecaster' : 'Diagnose Network PoW'}
                </Button>
              </div>
            </Card>

            {networkResult && (
              <div className="space-y-4 animate-in fade-in-50 duration-300">
                {/* Health Rating Banner */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6 text-emerald-600" />
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Consensus Health Rating</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {networkResult.networkHealthRating}
                      </div>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 text-xs px-3 py-1">
                    Block Cadence: {networkResult.projectedBlockTimeSec.toFixed(1)}s
                  </Badge>
                </div>

                {/* Difficulty & Consensus Risk */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mb-1">
                      <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                      Difficulty Adjustment Forecast
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {networkResult.difficultyAdjustmentForecast}
                    </div>
                  </div>
                  <div className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mb-1">
                      <Server className="w-3.5 h-3.5 text-teal-600" />
                      Decentralization & Share Distribution
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {networkResult.hashrateDistributionRisk}
                    </div>
                  </div>
                </div>

                {/* Optimal Worker Thread Allocation */}
                <Card className="p-4 border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/30 dark:bg-emerald-950/20">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Sliders className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                      <span className="font-semibold text-sm text-emerald-900 dark:text-emerald-200">
                        Optimal Worker Thread Concurrency Matrix
                      </span>
                    </div>
                    {onApplyOptimalThreads && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleApplyThreads}
                        className="text-xs border-emerald-300 text-emerald-800 dark:border-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 h-8"
                      >
                        {appliedThreads ? (
                          <>
                            <Check className="w-3.5 h-3.5 mr-1 text-emerald-600" />
                            Applied!
                          </>
                        ) : (
                          '1-Click Apply Config'
                        )}
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                    <div className="p-2.5 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Entry Mobile</div>
                      <div className="text-base font-bold text-gray-900 dark:text-white">
                        {networkResult.optimalWorkerThreadAllocation.entryClassThreads} Threads
                      </div>
                    </div>
                    <div className="p-2.5 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Balanced Rig</div>
                      <div className="text-base font-bold text-gray-900 dark:text-white">
                        {networkResult.optimalWorkerThreadAllocation.balancedClassThreads} Threads
                      </div>
                    </div>
                    <div className="p-2.5 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Extreme GPU/ASIC</div>
                      <div className="text-base font-bold text-gray-900 dark:text-white">
                        {networkResult.optimalWorkerThreadAllocation.extremeClassThreads} Threads
                      </div>
                    </div>
                    <div className="p-2.5 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
                      <div className="text-xs text-gray-500 dark:text-gray-400">Duty Cycle Cap</div>
                      <div className="text-base font-bold text-emerald-600 dark:text-emerald-400">
                        {networkResult.optimalWorkerThreadAllocation.throttleDutyCyclePercent}%
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Strategic Recommendations */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Strategic PoW Calibrations
                  </div>
                  <div className="space-y-2">
                    {networkResult.strategicRecommendations.map((rec, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs sm:text-sm"
                      >
                        <div className="space-y-0.5">
                          <span className="font-semibold text-emerald-700 dark:text-emerald-400 mr-2">
                            [{rec.pillar}]
                          </span>
                          <span className="text-gray-700 dark:text-gray-300">{rec.recommendation}</span>
                        </div>
                        <Badge
                          variant="secondary"
                          className="shrink-0 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 w-fit"
                        >
                          {rec.expectedHashrateDelta}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Executive Summary */}
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 text-xs text-gray-600 dark:text-gray-300 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <p>{networkResult.executiveSummary}</p>
                </div>
              </div>
            )}
          </TabsContent>

          {/* TAB 2: Thermal & Power Arbitrage */}
          <TabsContent value="thermal" className="space-y-4 pt-3">
            <Card className="p-4 bg-gradient-to-br from-teal-50/50 to-cyan-50/30 dark:from-teal-950/20 dark:to-cyan-950/10 border-teal-100 dark:border-teal-900/40">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase font-semibold text-teal-700 dark:text-teal-400 tracking-wider">
                    Hardware Cluster Telemetry
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Avg Temp:{' '}
                    <span className="font-semibold text-gray-900 dark:text-white">{averageTempC}°C</span> | Fleet
                    Power:{' '}
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {averagePowerW.toLocaleString()} W
                    </span>
                  </div>
                </div>
                <Button
                  onClick={handleRunThermalAnalysis}
                  disabled={thermalLoading}
                  className="bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20 flex items-center gap-2 shrink-0"
                >
                  <RefreshCw className={`w-4 h-4 ${thermalLoading ? 'animate-spin' : ''}`} />
                  {thermalResult ? 'Re-Run Thermal Doctor' : 'Analyze Rig Thermal Efficiency'}
                </Button>
              </div>
            </Card>

            {thermalResult && (
              <div className="space-y-4 animate-in fade-in-50 duration-300">
                {/* Efficiency KPI Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Compute Efficiency</div>
                    <div className="text-base font-bold text-teal-600 dark:text-teal-400">
                      {thermalResult.joulesPerMegahashEfficiency}
                    </div>
                  </div>
                  <div className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Mined INT Power Cost</div>
                    <div className="text-base font-bold text-emerald-600 dark:text-emerald-400">
                      {thermalResult.projectedPowerCostPerIntMined}
                    </div>
                  </div>
                  <div className="p-3.5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Renewable Ratio</div>
                    <div className="text-base font-bold text-cyan-600 dark:text-cyan-400">
                      {thermalResult.greenOffsetIndex.renewablePowerRatio}
                    </div>
                  </div>
                </div>

                {/* Thermal Throttling Guidance Table */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <Thermometer className="w-3.5 h-3.5 text-teal-600" />
                    Automated Thermal Governor & Throttle Thresholds
                  </div>
                  <div className="space-y-2">
                    {thermalResult.thermalThrottlingGuidance.map((bracket, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs sm:text-sm ${
                          bracket.safetyStatus === 'alert'
                            ? 'bg-red-50/50 dark:bg-red-950/20 border-red-200 dark:border-red-900/40'
                            : bracket.safetyStatus === 'warning'
                              ? 'bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40'
                              : 'bg-emerald-50/40 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-900/30'
                        }`}
                      >
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            <span>{bracket.temperatureBracket}</span>
                            <Badge variant="outline" className="text-xs font-normal">
                              Fan: {bracket.targetFanSpeed}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{bracket.throttleAction}</p>
                        </div>
                        <Badge
                          className={
                            bracket.safetyStatus === 'alert'
                              ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300'
                              : bracket.safetyStatus === 'warning'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                                : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                          }
                        >
                          {bracket.safetyStatus.toUpperCase()}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Green Offset & Eco Score */}
                <Card className="p-4 border-cyan-100 dark:border-cyan-900/40 bg-gradient-to-r from-cyan-50/40 to-teal-50/40 dark:from-cyan-950/20 dark:to-teal-950/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Leaf className="w-4 h-4 text-emerald-600" />
                      <span className="font-semibold text-sm text-cyan-900 dark:text-cyan-200">
                        Green Mining & Carbon Offset Index
                      </span>
                    </div>
                    <Badge className="bg-emerald-600 text-white text-xs">
                      Eco Score: {thermalResult.greenOffsetIndex.ecoScore}/100
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
                    <span>Offset Credits Accumulated:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {thermalResult.greenOffsetIndex.carbonOffsetKg.toLocaleString()} kg CO₂e
                    </span>
                  </div>
                </Card>

                {/* Doctor Verdict */}
                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 text-xs text-gray-600 dark:text-gray-300 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                  <p>{thermalResult.doctorVerdict}</p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

